import { memo } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { useAccount } from '@/components/auth/AccountContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getIcon } from '../utils/icons';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const AccountSwitcher = memo(function AccountSwitcher() {
  const { user: authUser, signOut } = useAuth();
  const { 
    currentAccountId, 
    accounts, 
    isLoading, 
    error,
    switchAccount,
    clearError 
  } = useAccount();
  const navigate = useNavigate();

  const ChevronDownIcon = getIcon('ChevronDown');
  const CheckIcon = getIcon('Check');
  const BuildingIcon = getIcon('Building2');
  const LoaderIcon = getIcon('Loader2');
  const LogOutIcon = getIcon('LogOut');

  // Encontrar conta atual
  const currentAccount = accounts.find(acc => acc.id === currentAccountId);

  const handleAccountSwitch = async (accountId: string) => {
    if (!authUser) return;
    
    // Limpar erro anterior se houver
    if (error) clearError();
    
    try {
      await switchAccount(accountId);
      toast.success('Conta alterada com sucesso!');
    } catch (error) {
      console.error('Erro ao trocar conta:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao trocar conta';
      toast.error(errorMessage);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success('Logout realizado com sucesso!');
      // O redirecionamento agora é feito automaticamente pela função signOut
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao fazer logout';
      toast.error(errorMessage);
    }
  };

  // Não renderizar se estiver carregando ou não tiver usuário
  if (isLoading || !authUser) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white hover:bg-opacity-10 transition-colors h-8 px-2 gap-2"
          disabled={isLoading}
          aria-label="Trocar conta ativa"
        >
          <BuildingIcon className="w-4 h-4" />
          <span className="max-w-[100px] truncate text-sm">
            {currentAccount?.name || 'Sem conta'}
          </span>
          {isLoading ? (
            <LoaderIcon className="w-4 h-4 animate-spin" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-64 bg-white border border-gray-200 shadow-lg"
        sideOffset={8}
      >
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Trocar Conta
          </p>
        </div>
        
        {accounts.map((account) => {
          const isActive = account.id === currentAccountId;
          
          return (
            <DropdownMenuItem
              key={account.id}
              onClick={() => handleAccountSwitch(account.id)}
              className={`px-3 py-2 cursor-pointer transition-colors ${
                isActive 
                  ? 'bg-emerald-50 text-emerald-700' 
                  : 'hover:bg-gray-50'
              }`}
              disabled={isActive || isLoading}
            >
              <div className="flex items-center gap-3 w-full">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <BuildingIcon className="w-4 h-4 text-emerald-600" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${
                    isActive ? 'font-semibold' : 'font-medium'
                  }`}>
                    {account.name}
                  </p>
                  {isActive && (
                    <p className="text-xs text-emerald-600">
                      Conta ativa
                    </p>
                  )}
                </div>
                
                {isActive && (
                  <CheckIcon className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                )}
              </div>
            </DropdownMenuItem>
          );
        })}
        
        <div className="px-3 py-2 border-t border-gray-100">
          <p className="text-xs text-gray-500 text-center">
            {accounts.length} contas disponíveis
          </p>
        </div>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem
          onClick={handleLogout}
          className="px-3 py-2 cursor-pointer transition-colors hover:bg-red-50 text-red-600 hover:text-red-700"
        >
          <div className="flex items-center gap-3 w-full">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <LogOutIcon className="w-4 h-4 text-red-600" />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                Sair
              </p>
              <p className="text-xs text-red-500">
                Fazer logout da conta
              </p>
            </div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

AccountSwitcher.displayName = 'AccountSwitcher';

export default AccountSwitcher;
